import React from "react";

/**
 * Compact rich-game-meta block for league cards.
 * Looks up the game by name in the catalog `games` array and renders
 * cover art + platform badges when IGDB metadata is available.
 * Falls back to a plain "🎮 GameName" line when not.
 *
 * Props:
 *  - gameName: string  (e.g. league.game)
 *  - games: array      (catalog from fetchGames)
 *  - size?: "sm" | "md"
 *  - dataTestid?: string
 */
export function GameMeta({ gameName, games, size = "md", dataTestid }) {
  const game = (games || []).find(
    (g) => g?.name && gameName && g.name.toLowerCase() === gameName.toLowerCase(),
  );
  const cover = game?.cover_url;
  const platforms = (game?.platforms || []).slice(0, 3);

  const coverCls = size === "sm" ? "w-7 h-9" : "w-10 h-14";

  if (!cover && platforms.length === 0) {
    return (
      <p data-testid={dataTestid} className="text-[#a3a3ac] flex items-center gap-2">
        <i className="fas fa-gamepad text-[#14b8a6]/80" />
        <span className="truncate">{gameName}</span>
      </p>
    );
  }

  return (
    <div data-testid={dataTestid} className="flex items-center gap-3 min-w-0">
      {cover ? (
        <img
          src={cover}
          alt=""
          loading="lazy"
          className={`${coverCls} object-cover rounded-md border border-[#14b8a6]/30 flex-shrink-0`}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className={`${coverCls} rounded-md bg-black/40 border border-[#14b8a6]/30 flex items-center justify-center flex-shrink-0`}>
          <i className="fas fa-gamepad text-[#14b8a6]/60" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-semibold truncate">{gameName}</p>
        {platforms.length > 0 && (
          <div className="mt-0.5 flex flex-wrap gap-1">
            {platforms.map((p) => (
              <span
                key={p}
                className="text-[9px] tracking-wider px-1.5 py-0.5 rounded bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/30"
              >
                {p}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default GameMeta;
